import { JSX, useContext } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useAuth } from 'hooks/useAuth';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  SelectOption,
  ensureCurrentValueOption,
  getMcleodIntegrationFromList,
  getRevenueCodes,
  normalizeCollectionMethodValue,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import { NormalizedLoad } from 'types/Load';
import { MaybeUndef } from 'types/UtilityTypes';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { devDisableRequiredFields } from 'utils/loadInfoAndBuilding';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';
import { RateGrid, RateParty } from '../components/RateGrid';
import { RateType } from './Specifications';

const MCLEOD_RATE_TYPE_OPTIONS = Object.keys(RateType).map((key) => ({
  value: key,
  label: key,
}));

export function RatesForm({
  formMethods,
  showCarrierFields,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  showCarrierFields: boolean; // Show in Load Info, read-only mode. Not in Load Building bc it's broken rn
}): JSX.Element {
  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);
  const { tmsIntegrations } = useServiceFeatures();
  const { user } = useAuth();

  const tms = getMcleodIntegrationFromList(tmsIntegrations);
  // Revenue code is not required for Tumalo
  const isRevenueCodeRequired = tms?.featureFlags.isRevenueCodeRequired;

  // const isSalesperson1Enabled = tms?.tenant.includes('fcfm'); // Fetch Freight only for now
  const isSalesperson1Enabled = false;
  const hasSalesperson1 = formMethods.watch('rateData.salesperson1');

  const {
    control,
    formState: { errors },
  } = formMethods;

  const revenueCodes = getRevenueCodes(tms);

  const collectionMethods: SelectOption[] = [
    { value: 'P', label: 'Prepaid' },
    { value: 'C', label: 'Collect' },
    { value: 'T', label: 'Third-Party' },
    { value: 'D', label: 'COD' },
  ];

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      {isSalesperson1Enabled && (
        <>
          <div className='col-span-2'>
            <Label
              hideAIHint={true}
              name={'rateData.salesperson1'}
              required={false}
            >
              Salesperson 1
            </Label>
            <Controller
              name='rateData.salesperson1'
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <div className='text-neutral-500'>
                  <Select
                    showSearch
                    disabled={false}
                    className='h-8'
                    placeholder={'Choose'}
                    optionFilterProp='children'
                    onChange={field.onChange}
                    value={field.value}
                    options={[
                      {
                        value: user?.external_tms_id ?? '',
                        label: user?.external_tms_id ?? '',
                      },
                    ]}
                  />
                </div>
              )}
            />
            <ErrorMessage
              errors={errors}
              name={'rateData.salesperson1'}
              render={({ message }: { message: string }) => (
                <Typography variant='body-xs' className='text-error-500'>
                  {message}
                </Typography>
              )}
            />
          </div>
          <div className='col-span-2'>
            <div className='relative'>
              <LoadBuildingTextInput
                name='rateData.salesperson1Percent'
                label='Percentage'
                readOnly={!hasSalesperson1}
                options={{
                  valueAsNumber: true,
                  min: 0,
                  max: 100,
                }}
                placeholder='(0-100)'
                inputClassName='pr-6'
              />
              <span className='absolute right-2 top-[30px] text-sm text-neutral-800 pointer-events-none'>
                %
              </span>
            </div>
          </div>
        </>
      )}
      {/* Customer Rates */}
      <>
        <div className='col-span-4 text-md text-neutral-800 font-semibold mb-1'>
          Customer
        </div>
        <div className='col-span-2'>
          <Label name={'rateData.collectionMethod'} required={true}>
            Collection Method
          </Label>
          <Controller
            name='rateData.collectionMethod'
            control={control}
            rules={{ required: devDisableRequiredFields ? false : 'Required' }}
            render={({ field }) => (
              <div className=' text-neutral-500'>
                <Select
                  showSearch
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={normalizeCollectionMethodValue(
                    field.value as MaybeUndef<string>,
                    collectionMethods
                  )}
                  options={ensureCurrentValueOption(
                    collectionMethods.map((method) => ({
                      value: method.label,
                      label: method.label,
                    })),
                    normalizeCollectionMethodValue(
                      field.value as MaybeUndef<string>,
                      collectionMethods
                    )
                  )}
                />
              </div>
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'rateData.collectionMethod'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
        <div className='col-span-2'>
          <Label
            name={'rateData.revenueCode'}
            required={isRevenueCodeRequired}
            // We always default this value so don't show AI hint unless there's a suggestion
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
          >
            Revenue Code
          </Label>
          <Controller
            name='rateData.revenueCode'
            control={control}
            rules={{
              required:
                devDisableRequiredFields || !isRevenueCodeRequired
                  ? false
                  : 'Required',
            }}
            render={({ field }) => (
              <div className=' text-neutral-500'>
                <Select
                  showSearch
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={field.value}
                  options={ensureCurrentValueOption(
                    revenueCodes?.map((code: SelectOption) => ({
                      value: code.value,
                      label: code.label,
                    })) ?? [],
                    field.value as MaybeUndef<string>
                  )}
                />
              </div>
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'rateData.revenueCode'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
        <RateGrid
          party={RateParty.Customer}
          formMethods={formMethods}
          rateTypeOptions={MCLEOD_RATE_TYPE_OPTIONS}
        />
      </>
      {/* Carrier Rates */}
      {/* FIXME: Mcleod PUT carrier rates and dispatch not working */}
      {showCarrierFields && (
        <>
          <hr className='col-span-4 my-1' />
          <div className='col-span-4 text-md text-neutral-800 font-semibold mb-1'>
            Carrier
          </div>

          <RateGrid
            party={RateParty.Carrier}
            formMethods={formMethods}
            rateTypeOptions={MCLEOD_RATE_TYPE_OPTIONS}
          />
        </>
      )}
    </Grid>
  );
}
